/**
 * Shared event date helpers.
 * Supports startDate/endDate with legacy eventDate / eventMonth / eventYear fallback.
 */

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const getOrdinal = (day) => {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const parseLocalDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // Jackson array form: [2026, 11, 26]
  if (Array.isArray(value) && value.length >= 3) {
    return new Date(value[0], value[1] - 1, value[2]);
  }

  // Prefer date-only parsing for "yyyy-MM-dd" to avoid timezone shifts
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Resolve effective start/end dates from an event object.
 */
export const resolveEventRange = (event = {}) => {
  const start =
    parseLocalDate(event.startDate) ||
    parseLocalDate(event.eventDate) ||
    (event.eventMonth && event.eventYear
      ? new Date(event.eventYear, event.eventMonth - 1, 15)
      : null);

  const end =
    parseLocalDate(event.endDate) ||
    parseLocalDate(event.startDate) ||
    parseLocalDate(event.eventDate) ||
    start;

  return { start, end };
};

/**
 * Format for UI:
 * - Single day:  "21st Aug 2026"
 * - Multi-day same month: "26th–29th Nov 2026"
 * - Multi-day cross month: "30th Nov–2nd Dec 2026"
 * - Month/year only: "Aug 2026"
 */
export const formatEventDisplayDate = (event = {}, options = {}) => {
  const { monthStyle = "short", withOrdinal = true } = options;
  const months = monthStyle === "long" ? MONTH_LONG : MONTH_SHORT;
  const { start, end } = resolveEventRange(event);

  if (!start) return "";

  // Month/year-only fallback (no exact day when only month/year existed)
  if (!event.startDate && !event.endDate && !event.eventDate && event.eventMonth && event.eventYear) {
    return `${months[event.eventMonth - 1]} ${event.eventYear}`;
  }

  const formatDay = (date) =>
    withOrdinal ? getOrdinal(date.getDate()) : String(date.getDate());

  const sameDay =
    end &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (!end || sameDay) {
    return `${formatDay(start)} ${months[start.getMonth()]} ${start.getFullYear()}`;
  }

  const sameMonthAndYear =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  if (sameMonthAndYear) {
    return `${formatDay(start)}–${formatDay(end)} ${months[start.getMonth()]} ${start.getFullYear()}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${formatDay(start)} ${months[start.getMonth()]}–${formatDay(end)} ${months[end.getMonth()]} ${end.getFullYear()}`;
  }

  return `${formatDay(start)} ${months[start.getMonth()]} ${start.getFullYear()}–${formatDay(end)} ${months[end.getMonth()]} ${end.getFullYear()}`;
};

/** Sort key: use startDate (or legacy equivalents). */
export const getEventSortDate = (event = {}) => {
  const { start } = resolveEventRange(event);
  return start || new Date(0);
};

/** Past if endDate (or start/legacy) is before today. */
export const isEventPast = (event = {}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { end, start } = resolveEventRange(event);
  const compare = end || start;
  if (!compare) return false;

  const d = new Date(compare);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

/** Upcoming if not past. */
export const isEventUpcoming = (event = {}) => !isEventPast(event);

/**
 * True when startDate is within the next `months` months (inclusive of today).
 */
export const isEventWithinMonths = (event = {}, months = 3) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { start } = resolveEventRange(event);
  if (!start) return false;

  const eventDate = new Date(start);
  eventDate.setHours(0, 0, 0, 0);

  const windowEnd = new Date();
  windowEnd.setMonth(windowEnd.getMonth() + months);
  windowEnd.setHours(23, 59, 59, 999);

  return eventDate >= today && eventDate <= windowEnd;
};

export default {
  getOrdinal,
  resolveEventRange,
  formatEventDisplayDate,
  getEventSortDate,
  isEventPast,
  isEventUpcoming,
  isEventWithinMonths,
};
